import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Message } from 'primereact/message'
import { useId, useRef, useState } from 'react'
import { useOfficiateDocument } from '~/hooks/documentsApi'
import useToast from '~/hooks/toast'
import type { Document } from '~/types/document.types'

export type DocumentOfficiationDialogProps = {
  document: Document
  visible: boolean
  onHide: () => void
}

export const DocumentOfficiationDialog = ({
  document: doc,
  visible,
  onHide,
}: DocumentOfficiationDialogProps) => {
  const toast = useToast()
  const officiateMutation = useOfficiateDocument(doc.id)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const nextLiveRevision = (doc.liveFile?.revision ?? 0) + 1

  const handleOfficiate = (file?: Blob) => {
    officiateMutation.mutate(
      { file, nextLiveVersion: nextLiveRevision },
      {
        onSuccess: () => {
          onHide()
          toast?.current?.show({ severity: 'success', summary: 'Document officiated successfully' })
        },
      },
    )
  }

  const currentStagingVersion = doc.stagingFile?.version?.toString() || '-'
  const currentLiveVersion = doc.liveFile?.revLabel || '-'
  const currentLiveDocNo = doc.liveDocNumber || '-'

  const nextRevLabel = nextLiveRevision.toString().padStart(3, '0')
  const nextLiveDocNo = doc.docNumberTemplate?.replace('{REV}', nextRevLabel) || '-'

  const isUploadMandatory = !doc.stagingFileId && !selectedFile
  const canSubmit = !isUploadMandatory
  const fileUploadId = useId()
  return (
    <Dialog
      header='Officiate Document'
      visible={visible}
      onHide={onHide}
      className='max-w-lg w-full'
      footer={
        <div className='flex gap-2 justify-end'>
          <Button label='Cancel' severity='secondary' onClick={onHide} />
          <Button
            label='Officiate'
            icon='pi pi-check'
            severity='success'
            disabled={!canSubmit}
            loading={officiateMutation.isLoading}
            onClick={() => handleOfficiate(selectedFile || undefined)}
          />
        </div>
      }
    >
      <div className='flex flex-col gap-6 py-2'>
        <p className='text-sm text-gray-600'>
          You are about to make this document <strong>Active</strong>. This will assign the next
          revision number and lock the template.
        </p>

        <div className='grid grid-cols-2 gap-4 text-sm'>
          <div className='flex flex-col gap-1 p-3 bg-gray-50 border rounded'>
            <span className='text-xs font-semibold text-gray-500 uppercase'>Current Status</span>
            <div className='flex flex-col gap-1 mt-1'>
              <div className='flex justify-between'>
                <span>Staging Version:</span>
                <span className='font-mono font-bold'>{currentStagingVersion}</span>
              </div>
              <div className='flex justify-between'>
                <span>Live Version:</span>
                <span className='font-mono font-bold'>{currentLiveVersion}</span>
              </div>
              <div className='flex flex-col gap-1 mt-1 border-t pt-1'>
                <span className='text-xs text-gray-400'>Live Doc Number:</span>
                <span
                  className='font-mono font-bold text-blue-700 truncate'
                  title={currentLiveDocNo}
                >
                  {currentLiveDocNo}
                </span>
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-1 p-3 bg-blue-50 border border-blue-100 rounded'>
            <span className='text-xs font-semibold text-blue-600 uppercase'>
              Next (Live) Status
            </span>
            <div className='flex flex-col gap-1 mt-1'>
              <div className='flex justify-between'>
                <span>Next Version:</span>
                <span className='font-mono font-bold text-blue-800'>{nextRevLabel}</span>
              </div>
              <div className='flex flex-col gap-1 mt-2 border-t border-blue-200 pt-1'>
                <span className='text-xs text-blue-400'>Next Doc Number:</span>
                <span className='font-mono font-bold text-blue-800 break-all' title={nextLiveDocNo}>
                  {nextLiveDocNo}
                </span>
              </div>
            </div>
          </div>
        </div>

        {isUploadMandatory && (
          <Message
            severity='warn'
            text='Mandatory: No staging draft exists. You must upload the signed final version to officiate.'
            className='w-full justify-start'
          />
        )}

        <div className='flex flex-col gap-2'>
          <label className='text-sm font-bold' htmlFor={fileUploadId}>
            {isUploadMandatory
              ? 'Upload Signed Final Version (Mandatory)'
              : 'Upload Signed Final Version (Optional)'}
          </label>
          <div className='flex items-center gap-2'>
            <input
              type='file'
              ref={fileInputRef}
              id={fileUploadId}
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              className='hidden'
            />
            <Button
              label={selectedFile ? selectedFile.name : 'Choose File'}
              icon='pi pi-upload'
              className='p-button-outlined p-button-sm grow'
              onClick={() => fileInputRef.current?.click()}
            />
            {selectedFile && (
              <Button
                icon='pi pi-times'
                severity='danger'
                className='p-button-text p-button-sm'
                onClick={() => setSelectedFile(null)}
              />
            )}
          </div>
          {!isUploadMandatory && !selectedFile && (
            <p className='text-xs text-gray-500 italic'>
              If no file is uploaded, the current staging draft (V{currentStagingVersion}) will be
              promoted.
            </p>
          )}
        </div>
      </div>
    </Dialog>
  )
}
